
import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { MdDelete, MdAdd, MdSort } from 'react-icons/md';
import { gql, useSuspenseQuery, useMutation } from '@apollo/client';
import * as countryCodes from 'country-codes-list';

import { withScreen } from './Screen';
import Pagination from './Pagination';
import { OrderList_GetOrdersQuery, OrderList_GetOrdersQueryVariables, OrderList_SyncAllOrdersFullfillmentsMutation, OrderList_SyncAllOrdersFullfillmentsMutationVariables, OrderList_CreateDirectSaleOrderMutation, OrderList_CreateDirectSaleOrderMutationVariables, OrderStatus, OrdersFilters } from '../../codegen/graphql';
import { ButtonGroup } from './common/ButtonGroup';
import { OrderRow } from './OrderRow';

import { ORDER_ROW_FRAGMENT } from './OrderRow';
import { Button } from './common/Button';
import { OrderFiltersBlock } from './OrderFiltersBlock';
import { useRequestManager } from './common/RequestManager';
import { Helmet } from 'react-helmet';

import JSURL from 'jsurl';

const GET_ORDERS = gql`
  query OrderList_GetOrders($page: Int, $pageSize: Int, $filters: OrdersFilters) {
    orders(page: $page, pageSize: $pageSize, filters: $filters) {
      nodes {
        ...OrderRow
      }
      total
      hasNextPage
      page
      pageSize
    }
  }
  ${ORDER_ROW_FRAGMENT}
`;

const SYNC_ALL_ORDERS_FULLFILLMENTS = gql`
  mutation OrderList_SyncAllOrdersFullfillments {
    syncAllOrdersFullfillments {
      successfulCount
      erroredCount
      leboncoinOrdersToSyncFromFrontend {
        ourOrderId
        externalOrderId
      }
    }
  }
`;

const CREATE_DIRECT_SALE_ORDER = gql`
  mutation OrderList_CreateDirectSaleOrder($input: CreateOrderInput!) {
    createDirectSaleOrder(input: $input) {
      id
    }
  }
`;

interface OrderListProps {}

function OrderList({}: OrderListProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackRequest } = useRequestManager();

  // Get params from URL directly
  const page = Number(searchParams.get('page') || '1');
  const sortBy = searchParams.get('sortBy') || 'orderDate';
  const sortOrder = searchParams.get('sortOrder') || 'DESC';


  const currentFilters: OrdersFilters = useMemo(() => (
    searchParams.get('filters')
      ? JSURL.parse(searchParams.get('filters')!) as OrdersFilters
      : {}
  ), [searchParams]);

  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [pageSize] = useState(20);
  
  const [syncAllOrdersFullfillmentsMutation, { loading: isSyncing }] = useMutation<
    OrderList_SyncAllOrdersFullfillmentsMutation,
    OrderList_SyncAllOrdersFullfillmentsMutationVariables
  >(SYNC_ALL_ORDERS_FULLFILLMENTS, {
    refetchQueries: [{ query: GET_ORDERS }]
  });

  const [createDirectSaleOrderMutation, { loading: isCreatingDirectSale }] = useMutation<
    OrderList_CreateDirectSaleOrderMutation,
    OrderList_CreateDirectSaleOrderMutationVariables
  >(CREATE_DIRECT_SALE_ORDER, {
    refetchQueries: [{ query: GET_ORDERS }]
  });

  const syncAllOrdersFullfillments = useCallback(async () => {
    await trackRequest(
      syncAllOrdersFullfillmentsMutation()
        .then(async ({ data, errors }) => {
          if (errors || !data) {
            throw errors;
          }
          
          if (data.syncAllOrdersFullfillments.leboncoinOrdersToSyncFromFrontend.length > 0) {
            // Syncing from frontend right now through browser extension, special case
            // await Promise.all(data.syncAllOrdersFullfillments.leboncoinOrdersToSyncFromFrontend.map((order) => (
            //   syncLeboncoinOrderFromFrontend(order.ourOrderId, order.externalOrderId)
            // )));
          }
        }),
      {
        title: 'Syncing all orders fullfillments',
        isBlocking: false,
      }
    );
  }, [syncAllOrdersFullfillmentsMutation]);

  const createDirectSaleOrder = useCallback(async () => {

    if (!confirm('Are you sure you want to create a direct sale order?')) {
      return;
    }

    await trackRequest(
      createDirectSaleOrderMutation({
        variables: {
          input: {
            orderDate: new Date().toISOString(),
            orderStatus: OrderStatus.Delivered,
            customerName: 'Direct Sale Customer',
            customerAddressStreetNumber: '',
            customerAddressLine1: '',
            customerAddressLine2: '',
            customerAddressCity: '',
            customerAddressState: '',
            customerAddressPostalCode: '',
            customerAddressCountry: 'France',
            customerAddressCountryCode: 'FR',
            customerAddressPhone: '',
            customerAddressPhoneCountryCode: '',
            customerOriginalAddressStreetNumber: '',
            customerOriginalAddressLine1: '',
            customerOriginalAddressLine2: '',
            customerOriginalAddressCity: '',
            customerOriginalAddressState: '',
            customerOriginalAddressPostalCode: '',
            customerOriginalAddressCountryCode: 'FR',
          }
        }
      }).then(({ data, errors }) => {
        if (errors || !data) {
          throw errors;
        }
        
        // Close modal and navigate to the newly created order
        navigate(`/orders/${data.createDirectSaleOrder.id}`);
      }),
      {
        title: 'Creating direct sale order',
        isBlocking: true,
      }
    );
  }, [createDirectSaleOrderMutation, trackRequest, navigate]);


  // Use URL params directly in the query - don't use state
  const { data, networkStatus } = useSuspenseQuery<
    OrderList_GetOrdersQuery,
    OrderList_GetOrdersQueryVariables
  >(
    GET_ORDERS,
    {
      fetchPolicy: 'cache-and-network',
      // returnPartialData: true,
      variables: {
        page,
        pageSize,
        filters: currentFilters,
      },
    }
  );

  const handleFiltersChange = useCallback((change: Partial<OrdersFilters>, allFilters: OrdersFilters) => {
    setSearchParams( prev => ({
      ...prev,
      filters: JSURL.stringify(allFilters),
    }));
  }, [setSearchParams]);

  const handleRowClick = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams( prev => ({
      ...prev,
      page: newPage.toString(),
    }));
  };

  // Calculate total pages
  const totalPages = Math.ceil(data.orders.total / data.orders.pageSize);

  const statusOptions = [
    { value: '', label: 'All' },
    { value: OrderStatus.PendingFullfillment, label: 'Need Fullfillment' },
    { value: OrderStatus.PendingShipment, label: 'Need Shipment' },
    { value: OrderStatus.Shipping, label: 'Shipping' },
    { value: OrderStatus.ShippingProblem, label: 'Shipping Problem' },
    { value: OrderStatus.ShippingPendingPickup, label: 'Pending Pickup' },
    { value: OrderStatus.Delivered, label: 'Delivered' },
  ];

  const countryOptions = [
    { value: '', label: 'All Countries' },
    { value: 'FR', label: `${countryCodes.findOne('countryCode', 'FR')?.flag} France` },
    { value: 'IT', label: `${countryCodes.findOne('countryCode', 'IT')?.flag} Italy` },
    { value: 'ES', label: `${countryCodes.findOne('countryCode', 'ES')?.flag} Spain` },
    { value: 'BE', label: `${countryCodes.findOne('countryCode', 'BE')?.flag} Belgium` },
    { value: 'DE', label: `${countryCodes.findOne('countryCode', 'DE')?.flag} Germany` },
    { value: 'CH', label: `${countryCodes.findOne('countryCode', 'CH')?.flag} Switzerland` },
    { value: 'FI', label: `${countryCodes.findOne('countryCode', 'FI')?.flag} Finland` },
    { value: 'HR', label: `${countryCodes.findOne('countryCode', 'HR')?.flag} Croatia` },
    { value: 'PT', label: `${countryCodes.findOne('countryCode', 'PT')?.flag} Portugal` },
  ];

  const handleSortChange = () => {
    const newSortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSearchParams( prev => ({
      ...prev,
      sortOrder: newSortOrder,
      sortBy: 'orderDate',
    }));
  };

  return (
    <>
      <Helmet>
        <title>Orders</title>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">

        <OrderFiltersBlock
          currentFilters={currentFilters}
          onFiltersChange={handleFiltersChange}
        />

        {/* Status Filter and Sort */}
        <div className="mb-4 flex justify-between items-start">
          <div className="flex flex-wrap gap-4">
            <ButtonGroup
              options={statusOptions}
              value={currentFilters.status || ''}
              onChange={(value) => {
                handleFiltersChange({ status: value as OrderStatus }, {
                  ...currentFilters,
                  status: value as OrderStatus,
                });
              }}
              name="status"
              className="mb-4"
            />
          </div>
          <Button
            onClick={handleSortChange}
            preset="white"
          >
            <MdSort size={20} />
            Sort by Date {sortOrder === 'ASC' ? '↑' : '↓'}
          </Button>
        </div>
        
        <div className="flex justify-between items-center mb-6">
          <div className="flex flex-row gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Orders ({data.orders.total})</h1>
            <Button
              onClick={() => syncAllOrdersFullfillments()}
              disabled={isSyncing}
            >
              {isSyncing ? 'Syncing...' : 'Sync All Fulfillments'}
            </Button>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => createDirectSaleOrder()}
              disabled={isCreatingDirectSale}
              preset="primary"
            >
              <MdAdd size={20} />
              Add Direct Sale
            </Button>
            {selectedOrders.size > 0 && (
              <button
                onClick={() => alert('Delete functionality not implemented')}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                <MdDelete size={20} />
                Delete Selected
              </button>
            )}

            {/* <button
              onClick={() => navigate('/orders/new')}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <MdAdd size={20} />
              New Order
            </button> */}
          </div>
        </div>

        <Pagination
          currentPage={data.orders.page}
          totalPages={totalPages}
          totalItems={data.orders.total}
          pageSize={data.orders.pageSize}
          hasNextPage={data.orders.hasNextPage}
          onPageChange={handlePageChange}
        />

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Header Row */}
          <div className="grid grid-cols-12 bg-gray-50 divide-x divide-gray-200">
            <div className="col-span-2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Order Info
            </div>
            <div className="col-span-2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customer
            </div>
            <div className="col-span-2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </div>
            <div className="col-span-5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Items
            </div>
            <div className="col-span-1 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </div>
          </div>
          
          {/* Data Rows */}
          <div className="bg-white divide-y divide-gray-200">
            {data.orders.nodes.length === 0 ? (
              <div className="px-6 py-4 text-center text-gray-500">
                {currentFilters.search ? 'No orders match your search criteria' : 'No orders found'}
              </div>
            ) : (
              data.orders.nodes.map((order) => (<>
                <OrderRow
                  key={order.id}
                  order={order}
                  handleRowClick={handleRowClick}
                />
              </>))
            )}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={data.orders.page}
            totalPages={totalPages}
            totalItems={data.orders.total}
            pageSize={data.orders.pageSize}
            hasNextPage={data.orders.hasNextPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <Outlet />
    </>
  );
}

export default withScreen(OrderList); 